import type { Ref } from "react";
import { forwardRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

import type { ISearchInputProps } from "./types";

import { Input } from "@/components/formControls/input";
import { useDebounced, useReadOnly } from "@/hooks";
import { Icon } from "@/components/atoms/icons";

export const SearchInput = forwardRef(function SearchInput(
  props: ISearchInputProps,
  ref: Ref<HTMLInputElement>,
) {
  const {
    value = "",
    onDebouncedValueChange,
    defaultValue,
    onIconClick,
    onIconMouseDown,
    onChangeValue,
    readOnly: propsReadOnly,
    disabled,
    searchIconType = "image",
    icon,
    ...rest
  } = props;
  const readOnly = useReadOnly(propsReadOnly);
  const debouncedValue = useDebounced(value, 500);

  const { t } = useTranslation("tableSearch");

  useEffect(() => {
    onDebouncedValueChange?.(debouncedValue || value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  const canDelete = !(value === "" || readOnly || disabled);

  function clear() {
    if (canDelete) {
      onDebouncedValueChange?.("");
      onChangeValue?.("");
      onIconClick?.("delete");
    } else {
      onIconClick?.("search");
    }
  }

  return (
    <Input
      ref={ref}
      iconType={canDelete ? "button" : searchIconType}
      icon={icon || (canDelete ? CloseCircle : Magnifier)}
      onIconClick={clear}
      onChangeValue={onChangeValue}
      onIconMouseDown={(event) => {
        // prevent input focusout
        event.preventDefault();
        onIconMouseDown?.(event);
      }}
      placeholder={t("default")}
      value={value}
      readOnly={readOnly}
      disabled={disabled}
      {...rest}
    />
  );
});

function CloseCircle() {
  return <Icon name={"CloseCircle"} />;
}
function Magnifier() {
  return <Icon name={"Magnifer"} />;
}
